import { useCallback, useEffect, useMemo, useState } from 'react';
import { time, projects as projectApi } from '../api/client';
import { isoDate, addDays } from '../util/dates';
import PunchClock from '../components/PunchClock';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];


/** Employee self-service: punch clock plus the weekly project grid. */
export default function MyTime({ me, onError, onNotice }) {
  const [anchor, setAnchor] = useState(isoDate());
  const [week, setWeek] = useState(null);
  const [bookable, setBookable] = useState([]);
  const [today, setToday] = useState(null);
  const [grid, setGrid] = useState({});      // `${projectId}|${date}` -> hours
  const [rows, setRows] = useState([]);      // project ids in the grid
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, p, t] = await Promise.all([
        time.week(anchor),
        projectApi.bookable(me.employeeId),
        time.today(),
      ]);
      setWeek(w);
      setBookable(p || []);
      setToday(t);
      setNote(w.employeeNote || '');

      const g = {};
      const ids = [];
      (w.entries || []).forEach((e) => {
        g[`${e.projectId}|${e.workDate}`] = e.hours;
        if (!ids.includes(e.projectId)) ids.push(e.projectId);
      });
      setGrid(g);
      setRows(ids.length ? ids : (p && p.length ? [p[0].id] : []));
    } catch (e) {
      onError(e.message);
    }
  }, [anchor, me.employeeId, onError]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    if (!week) return [];
    return Array.from({ length: 7 }, (_, i) => addDays(week.weekStart, i));
  }, [week]);

  const modeByDay = useMemo(() => {
    const m = {};
    ((week && week.attendance) || []).forEach((a) => { if (a.workMode) m[a.workDate] = a.workMode; });
    return m;
  }, [week]);
  const officeDays = Object.values(modeByDay).filter((v) => v === 'OFFICE').length;
  const homeDays = Object.values(modeByDay).filter((v) => v === 'HOME').length;

  const dayTotal = (d) =>
    rows.reduce((s, pid) => s + (parseFloat(grid[`${pid}|${d}`]) || 0), 0);
  const rowTotal = (pid) =>
    days.reduce((s, d) => s + (parseFloat(grid[`${pid}|${d}`]) || 0), 0);
  const grandTotal = days.reduce((s, d) => s + dayTotal(d), 0);

  async function punch(kind, mode) {
    setBusy(true);
    try {
      if (kind === 'in') {
        await time.punchIn(mode, '');
        onNotice(`Punched in · ${mode === 'HOME' ? 'working from home' : 'working from office'}. The timer is running.`);
      } else {
        const r = await time.punchOut();
        onNotice(r && r.workedHours != null ? `Punched out · ${r.workedHours}h worked today` : 'Punched out');
      }
      await load();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function switchMode(mode) {
    setBusy(true);
    try {
      await time.setMode(mode);
      onNotice(`Changed today to ${mode === 'HOME' ? 'work from home' : 'work from office'}`);
      await load();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function save(thenSubmit) {
    setBusy(true);
    try {
      const entries = [];
      rows.forEach((pid) =>
        days.forEach((d) => {
          const v = parseFloat(grid[`${pid}|${d}`]);
          if (v > 0) entries.push({ projectId: pid, workDate: d, hours: v });
        })
      );
      await time.saveWeek(anchor, entries, note);
      if (thenSubmit) {
        await time.submitWeek(anchor);
        onNotice('Timesheet submitted for approval');
      } else {
        onNotice('Saved');
      }
      await load();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!week) return <p className="muted">Loading…</p>;

  const editable = week.editable;

  return (
    <div>
      <PunchClock
        today={today}
        busy={busy}
        onPunchIn={(mode) => punch('in', mode)}
        onPunchOut={() => punch('out')}
        onSwitchMode={switchMode}
      />

      <div className="row-between">
        <div>
          <h2>Timesheet</h2>
          <p className="muted">
            Week of {week.weekStart} · booked <strong>{grandTotal.toFixed(1)}h</strong>
            {week.attendanceHours > 0 && <> · clocked {week.attendanceHours}h</>}
            {(officeDays + homeDays) > 0 && <> · {officeDays} office / {homeDays} home</>}
            {' · '}<span className={`pill pill-${String(week.status).toLowerCase()}`}>{week.status}</span>
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-sm" onClick={() => setAnchor(addDays(week.weekStart, -7))}>← Prev</button>
          <button className="btn btn-sm" onClick={() => setAnchor(isoDate())}>This week</button>
          <button className="btn btn-sm" onClick={() => setAnchor(addDays(week.weekStart, 7))}>Next →</button>
        </div>
      </div>

      {week.decisionNote && (
        <div className={`alert ${week.status === 'REJECTED' ? 'alert-error' : 'alert-ok'}`}>
          Manager note: {week.decisionNote}
        </div>
      )}

      <div className="table-wrap">
        <table className="grid-table">
          <thead>
            <tr>
              <th style={{ minWidth: 190 }}>Project</th>
              {days.map((d, i) => (
                <th key={d} className="num">
                  {DAY_LABELS[i]}<div className="small muted">{d.slice(8)}</div>
                  {modeByDay[d] && (
                    <div className={`mode-dot mode-${modeByDay[d].toLowerCase()}`}>
                      {modeByDay[d] === 'HOME' ? 'WFH' : 'WFO'}
                    </div>
                  )}
                </th>
              ))}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((pid) => {
              const p = bookable.find((x) => x.id === pid);
              return (
                <tr key={pid}>
                  <td>
                    <select
                      value={pid}
                      disabled={!editable}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setRows(rows.map((r) => (r === pid ? next : r)));
                        const moved = { ...grid };
                        days.forEach((d) => {
                          const v = moved[`${pid}|${d}`];
                          if (v !== undefined) {
                            moved[`${next}|${d}`] = v;
                            delete moved[`${pid}|${d}`];
                          }
                        });
                        setGrid(moved);
                      }}
                    >
                      {bookable.map((x) => (
                        <option key={x.id} value={x.id}>{x.code} · {x.name}</option>
                      ))}
                    </select>
                    {p && p.clientName && <div className="small muted">{p.clientName}</div>}
                  </td>
                  {days.map((d) => (
                    <td key={d} className="num">
                      <input
                        className="hours"
                        type="number" min="0" max="24" step="0.5"
                        disabled={!editable}
                        value={grid[`${pid}|${d}`] ?? ''}
                        onChange={(e) => setGrid({ ...grid, [`${pid}|${d}`]: e.target.value })}
                      />
                    </td>
                  ))}
                  <td className="num"><strong>{rowTotal(pid).toFixed(1)}</strong></td>
                </tr>
              );
            })}
            <tr className="totals">
              <td>Daily total</td>
              {days.map((d) => {
                const t = dayTotal(d);
                return <td key={d} className={`num ${t > 24 ? 'over' : ''}`}>{t ? t.toFixed(1) : '—'}</td>;
              })}
              <td className="num"><strong>{grandTotal.toFixed(1)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {editable && (
        <>
          <div className="actions" style={{ marginTop: 12 }}>
            <button className="btn btn-sm" disabled={!bookable.length}
                    onClick={() => setRows([...rows, bookable.find((b) => !rows.includes(b.id))?.id ?? bookable[0].id])}>
              + Add project row
            </button>
          </div>
          <label className="field" style={{ marginTop: 12 }}>
            <span>Note for your manager</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="actions" style={{ marginTop: 12 }}>
            <button className="btn" disabled={busy} onClick={() => save(false)}>Save draft</button>
            <button className="btn btn-primary" disabled={busy || grandTotal <= 0}
                    onClick={() => save(true)}>Submit for approval</button>
          </div>
        </>
      )}
    </div>
  );
}
